import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { AuthProvider } from '@features/auth/AuthContext';
import { useSyncPreferredLanguage } from '@features/auth/useSyncPreferredLanguage';
import { createQueryClient } from '@shared/api/queryClient';
import { ErrorState } from '@shared/ui/StateMessages';
import { router } from './router';

/**
 * Tiny side-effect component mounted inside AuthProvider — runs the
 * post-auth language sync to /api/users/me/preferences.
 * Renders nothing.
 */
function AuthSideEffects(): null {
  useSyncPreferredLanguage();
  return null;
}

const queryClient = createQueryClient();

function RootError({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
}) {
  const { t } = useTranslation();
  // Surface the actual error name + message + first stack frame so future
  // failures don't reduce to "Error" with no detail. Codex iter-2 review
  // would have wanted us to fail loudly, not silently.
  const detail =
    error instanceof Error
      ? `${error.name}: ${error.message || '(no message)'}\n${(error.stack ?? '').split('\n').slice(0, 4).join('\n')}`
      : String(error);
  if (typeof console !== 'undefined') console.error('[RootError]', error);
  return (
    <div className="grid min-h-screen place-items-center px-6">
      <ErrorState title={t('common.error_generic')} description={detail} onRetry={resetErrorBoundary} />
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <ErrorBoundary FallbackComponent={RootError}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthSideEffects />
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
